import {FormControl, FormHelperText, Select, SelectProps, Theme} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {FormattedMessage} from "react-intl";
import {CheckMenuItem, MultipleLineError, useErrorMessages} from "@dvrt/graphql-widgets";
import {useMemo} from "react";
import {QuotaInfo} from "./EndpointCreateDialog";

const useStyles = makeStyles()((theme: Theme) => ({
    packagePropLabel: {
        marginLeft: "0.5em",
    },
    packageTitle: {
        fontWeight: "bold",
    },
    packageProps: {
        fontSize: "0.8em",
        opacity: 0.9,
    },
    errorList: {
        fontSize: "0.7em",
        paddingLeft: "2em",
        color: theme.palette.error.dark,
    },
}));

const FormattedItemName = ({name, prefix = ""}: {name: string; prefix?: string}) => (
    // we provide translations for custom quota type names here
    // eslint-disable-next-line formatjs/enforce-id, formatjs/enforce-default-message
    <FormattedMessage id={prefix + name} defaultMessage={name} />
);

const DisplayError = ({error}: {error: QuotaSelectorItem["allocationIncrementError"]}) => {
    const {classes} = useStyles();
    const errorMessages = useErrorMessages();

    const messageDescriptor = errorMessages[error.type];

    // just in case
    if (!messageDescriptor) {
        throw new Error(`No message descriptor found for error type: ${error.type}`);
    }

    return (
        <ul className={classes.errorList}>
            <li>
                <FormattedMessage {...messageDescriptor} values={error.context} />
            </li>
        </ul>
    );
};

type QuotaItemSelectorProps<Value extends string | number = string> = {
    value: Value;
    updateValue: (v: Value) => void;
    items: QuotaSelectorItem<Value>[] | Value[];
    disabled?: boolean;
    variant?: SelectProps["variant"];
    style?: React.CSSProperties;
    initialValue?: Value;
    fullWidth?: boolean;
    noErrorForInitialValue?: boolean;
    itemNamePrefix?: string;
};

/** Represents a quota item with limit quota information */
export type QuotaSelectorItem<Value extends string | number = string> = {
    value: Value;
} & Omit<QuotaInfo, "remaining">;

/** A select component that displays quota items with additional information */
const QuotaItemSelector = <Value extends string | number = string>({
    disabled,
    items,
    updateValue,
    value,
    variant = "standard",
    style,
    initialValue,
    fullWidth,
    noErrorForInitialValue = false,
    itemNamePrefix = "",
}: QuotaItemSelectorProps<Value>) => {
    const {classes} = useStyles();

    const convertedItems = useMemo(
        () =>
            items.map((item: QuotaSelectorItem<Value> | Value) => {
                if (typeof item === "string" || typeof item === "number") {
                    return {
                        value: item.toString(),
                    } as QuotaSelectorItem<string>;
                }

                return {
                    ...(item as QuotaSelectorItem),
                    value: item.value.toString(),
                } as QuotaSelectorItem<string>;
            }),
        [items],
    );

    const currentItem = convertedItems.find((item) => item.value === value.toString());
    console.log("current item", currentItem, value, convertedItems);

    const canShowErrorForCurrentItem = !(noErrorForInitialValue && initialValue === value);

    return (
        <FormControl fullWidth={fullWidth} error={canShowErrorForCurrentItem && !!currentItem.allocationIncrementError}>
            <Select
                style={style}
                variant={variant}
                margin="dense"
                fullWidth={fullWidth}
                value={value}
                onChange={(event) => updateValue(event.target.value as Value)}
                renderValue={(val) => <FormattedItemName prefix={itemNamePrefix} name={val.toString()} />}
                disabled={disabled}
            >
                {convertedItems.map((item, idx) => {
                    const hasInfo = item.domainUsed !== undefined && item.domainAllocated !== undefined;

                    return (
                        <CheckMenuItem
                            key={idx}
                            value={item.value}
                            disabled={!!item.allocationIncrementError && initialValue !== item.value}
                        >
                            <div>
                                <div className={hasInfo ? classes.packageTitle : undefined}>
                                    <FormattedItemName prefix={itemNamePrefix} name={item.value} />
                                </div>
                                {hasInfo && (
                                    <>
                                        <div className={classes.packageProps}>
                                            <FormattedMessage id="extensionLimits.used" defaultMessage="used" />:{" "}
                                            {item.domainUsed ?? 0}
                                            {item.domainAllocated !== undefined && (
                                                <>
                                                    ,
                                                    <span className={classes.packagePropLabel}>
                                                        <FormattedMessage
                                                            id="extensionLimits.allocated"
                                                            defaultMessage="allocated"
                                                        />
                                                        : {item.domainAllocated}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                        {!!item.allocationIncrementError &&
                                            !(noErrorForInitialValue && initialValue === item.value) && (
                                                <DisplayError error={item.allocationIncrementError} />
                                            )}
                                    </>
                                )}
                            </div>
                        </CheckMenuItem>
                    );
                })}
            </Select>
            <FormHelperText>
                {canShowErrorForCurrentItem && (
                    <MultipleLineError
                        errors={
                            currentItem.allocationIncrementError ? [currentItem.allocationIncrementError] : undefined
                        }
                    />
                )}
            </FormHelperText>
        </FormControl>
    );
};

export default QuotaItemSelector;
